import { useEffect } from 'react'
import { useProgram } from '@/api/queries'
import { StatusBadge } from '@/components/ui/Badge'
import type { ComboboxOption } from '@/components/ui/Combobox'
import { Field, Input, Select } from '@/components/ui/Field'
import { ReviewDrawer } from '@/components/review/ReviewDrawer'
import { useEntityDraft } from '@/components/review/useEntityDraft'
import type { Program, ProgramPatch, WbProgram, WbProgramTarget } from '@/types'
import { levelLabel, programLabel } from './columns'
import { isGraduate } from './translations'
import { isValidProgramUrl, ProgramDetails, ProgramPageField, WbProgramAssignField } from './ProgramFields'

/**
 * One crawled program: a degree. What the team edits here is its page and
 * tags, and which WB program it sits under; the WB program's name, segment
 * and slate mapping are the WB program drawer's (PC-33). The rest is what the
 * crawl extracted and stays as found (`name` and `degree` are the entity's
 * identity). The level is the team's to correct (PC-25): graduate moves the
 * program to the Graduate tab, out of its WB program. Adding one by hand
 * ("Add degree") asks for the name, degree and level too (APP-136) and puts
 * it under `addTo`.
 */
interface Draft {
  name: string
  degree: string
  level: string
  programUrl: string
  tags: string[]
}

const DEFAULTS: Draft = { name: '', degree: '', level: '', programUrl: '', tags: [] }

/** `program_level`'s values, as the programs agent writes them. */
const LEVELS = ['undergraduate', 'graduate', 'certificate'] as const

export function ProgramDrawer({
  clientId,
  program,
  addTo,
  wbOptions,
  tagPool,
  onClose,
  onSave,
  onAssign,
  onReview,
  onFlag,
}: {
  clientId: string
  program: Program | 'new' | null
  /** The WB program a new degree goes under. */
  addTo?: WbProgram | null
  wbOptions: ComboboxOption[]
  tagPool: string[]
  onClose: () => void
  onSave: (patch: ProgramPatch) => Promise<void>
  onAssign: (programId: string, target: WbProgramTarget) => void
  onReview: (id: string, status: 'approved' | 'rejected') => void
  onFlag: (id: string, flagged: boolean) => void
}) {
  const creating = program === 'new'
  const existing = creating ? null : program
  const { data: detail } = useProgram(clientId, existing?.id ?? null)

  const draft = useEntityDraft<Draft>(
    existing
      ? {
          name: existing.name,
          degree: existing.degree ?? '',
          level: existing.level ?? '',
          programUrl: existing.programUrl ?? '',
          tags: existing.tags,
        }
      : null,
    DEFAULTS,
  )

  const { reset, patch } = draft
  useEffect(() => {
    if (!program) reset()
    else if (creating && addTo) patch({ name: addTo.name })
  }, [program, creating, addTo, reset, patch])

  if (!program) return null
  const urlValid = isValidProgramUrl(draft.value.programUrl)

  function close() {
    draft.reset()
    onClose()
  }

  function save() {
    const { name, degree, level, tags } = draft.value
    const programUrl = draft.value.programUrl.trim() || null
    // The level is sent only when changed: once sent, it is the team's, not the crawl's.
    const levelChanged = existing !== null && level !== '' && level !== (existing.level ?? '')
    // `name` and `degree` are only sent on create: they are the crawled identity of an existing row.
    void onSave(
      existing
        ? { id: existing.id, programUrl, tags, ...(levelChanged ? { level } : {}) }
        : {
            name,
            degree: degree.trim() || null,
            level: level || null,
            programUrl,
            tags,
            wbProgramId: addTo?.id ?? null,
          },
    )
  }

  return (
    <ReviewDrawer
      open
      onClose={close}
      title={creating ? `Add a degree${addTo ? ` to ${addTo.name}` : ''}` : existing ? programLabel(existing) : ''}
      subtitle={
        existing && (
          <span className="flex items-center gap-2">
            <StatusBadge status={existing.status} />
            {existing.degree && <span className="text-ink-faint">{existing.degree}</span>}
          </span>
        )
      }
      creating={creating}
      entity={existing}
      saveLabel={creating ? 'Add degree' : 'Save changes'}
      saveDisabled={(creating && !draft.value.name.trim()) || !urlValid}
      onSave={save}
      onApprove={() => existing && onReview(existing.id, 'approved')}
      onReject={() => existing && onReview(existing.id, 'rejected')}
      onFlag={(next) => existing && onFlag(existing.id, next)}
      tags={draft.value.tags}
      tagPool={tagPool}
      onTagsChange={(tags) => draft.set('tags', tags)}
    >
      {existing && !isGraduate(existing) && (
        <WbProgramAssignField
          value={existing.wbProgramId}
          options={wbOptions}
          onAssign={(target) => onAssign(existing.id, target)}
        />
      )}

      {creating && (
        <>
          <Field label="Program name" required hint="As the school names it, without the degree.">
            {(p) => <Input {...p} value={draft.value.name} onChange={(e) => draft.set('name', e.target.value)} />}
          </Field>
          <Field label="Degree" hint="BA, BS, Minor, Certificate. “Bachelor of Science” is stored as BS.">
            {(p) => <Input {...p} value={draft.value.degree} onChange={(e) => draft.set('degree', e.target.value)} />}
          </Field>
        </>
      )}

      {/* A degree added to a WB program is undergraduate (ADR-0022). */}
      {!(creating && addTo) && (
        <Field
          label="Level"
          hint={
            existing
              ? 'Graduate moves it to the Graduate tab, out of its WB program. The next crawl keeps the level set here.'
              : undefined
          }
        >
          {(p) => (
            <Select {...p} value={draft.value.level} onChange={(e) => draft.set('level', e.target.value)}>
              {/* A level once set cannot be cleared, so "Not stated" is offered only while it is. */}
              {(creating || !existing?.level) && <option value="">Not stated</option>}
              {LEVELS.map((level) => (
                <option key={level} value={level}>
                  {levelLabel(level)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}

      <ProgramPageField value={draft.value.programUrl} onChange={(v) => draft.set('programUrl', v)} />

      {existing && <ProgramDetails program={existing} detail={detail} levelEditable />}
    </ReviewDrawer>
  )
}
